/**
 * Validator Name Provenance — UI consumer for Lane V.2 (WP #4084).
 *
 * Backend (Lane V.1 / WP-4083 / docs/adr/2026-05-20-validator-metadata-enrichment.md)
 * tags every validator in `/api/validators/list` with a `nameProvenance` field.
 *
 * Allowed backend values: `'config' | 'keybase' | 'proxy' | 'fallback'`.
 *
 * UI taxonomy (WP #4084) collapses the four backend tiers into THREE display
 * tiers — the user-facing trust signal:
 *
 *   verified   ← config | keybase   (signed / registered metadata)
 *   inferred   ← proxy              (heuristic: provider + country + stake rank)
 *   unverified ← fallback | null    (no info — pubkey only)
 *
 * AAAA contract:
 *   - When the API doesn't send a recognised provenance value, default to
 *     `unverified`. We NEVER claim verification we can't prove.
 *   - The synthetic `"Validator <prefix>..."` placeholder is dead. If a name
 *     string happens to match the legacy shape, it is downgraded to
 *     `unverified` regardless of provenance.
 */

// -----------------------------------------------------------------------------
// Backend contract — kept in lockstep with server/services/validatorNameEnrichment.js
// -----------------------------------------------------------------------------
export type NameProvenance = 'config' | 'keybase' | 'proxy' | 'fallback';

const BACKEND_PROVENANCES = new Set<NameProvenance>([
  'config',
  'keybase',
  'proxy',
  'fallback',
]);

// -----------------------------------------------------------------------------
// UI taxonomy — three tiers, what we render
// -----------------------------------------------------------------------------
export type NameTier = 'verified' | 'inferred' | 'unverified';

/**
 * Legacy daemon placeholder, e.g. "Validator 46WgaaB2...".
 * Lane V.1 removed the producer, but defense-in-depth keeps us safe against
 * any cached payload still in flight during deploy.
 */
export const LEGACY_PLACEHOLDER_REGEX = /^Validator [A-Za-z0-9]{6,12}\.\.\.$/;

/**
 * Runtime guard: validates that a raw value is a recognised backend tier.
 * Anything else — `undefined`, `null`, `'foo'`, a number — is rejected.
 *
 * The boundary cast pattern: prefer this over `as NameProvenance`.
 */
export function isNameProvenance(value: unknown): value is NameProvenance {
  return typeof value === 'string' && BACKEND_PROVENANCES.has(value as NameProvenance);
}

/**
 * Maps the 4-value backend provenance to the 3-value UI tier.
 * Unknown / null / missing → `unverified` (safe default).
 */
export function tierFromProvenance(provenance: unknown): NameTier {
  if (!isNameProvenance(provenance)) return 'unverified';
  switch (provenance) {
    case 'config':
    case 'keybase':
      return 'verified';
    case 'proxy':
      return 'inferred';
    case 'fallback':
      return 'unverified';
  }
}

/**
 * Truncated pubkey, e.g. "Mxck…7Hgy".
 * Uses a single-character ellipsis (U+2026) — narrower than "..." and stable
 * across fonts.
 */
export function truncatePubkey(pubkey: string | null | undefined, head = 4, tail = 4): string {
  if (!pubkey) return '';
  if (pubkey.length <= head + tail + 1) return pubkey;
  return `${pubkey.slice(0, head)}…${pubkey.slice(-tail)}`;
}

export interface ValidatorLabel {
  /** What to render as the primary label. Always non-empty. */
  text: string;
  /** UI tier — drives badge colour + treatment. */
  tier: NameTier;
  /** Raw backend provenance value, for telemetry / debugging. May be null. */
  provenance: NameProvenance | null;
  /** Whether the label is the truncated pubkey (vs a real / inferred name). */
  isPubkey: boolean;
  /** Long-form tooltip text for the badge — human-readable. */
  tooltip: string;
}

interface FormatInput {
  name?: string | null;
  pubkey?: string | null;
  nodePubkey?: string | null;
  nameProvenance?: unknown;
}

/**
 * The single helper every UI surface uses to render a validator label
 * consistently. Returns the visible text + the tier + a tooltip.
 *
 * Rules:
 *   1. If `nameProvenance` is unrecognised → unverified tier, render
 *      truncated pubkey.
 *   2. If `name` is null/empty → unverified tier, render truncated pubkey
 *      (regardless of provenance — the backend may have lied).
 *   3. If `name` matches the legacy placeholder regex → unverified tier,
 *      render truncated pubkey (defense in depth).
 *   4. Otherwise, render the name + tier mapped from provenance.
 */
export function formatValidatorLabel(input: FormatInput): ValidatorLabel {
  const provenance = isNameProvenance(input.nameProvenance) ? input.nameProvenance : null;
  const pubkey = input.pubkey ?? input.nodePubkey ?? '';
  const name = typeof input.name === 'string' ? input.name.trim() : '';

  // Defense in depth — placeholder strings are NEVER trusted as a name.
  const nameIsPlaceholder = name && LEGACY_PLACEHOLDER_REGEX.test(name);

  if (!name || nameIsPlaceholder) {
    return {
      text: truncatePubkey(pubkey),
      tier: 'unverified',
      provenance,
      isPubkey: true,
      tooltip:
        'Unverified — no registered name. ' +
        'This validator has not published on-chain Config metadata. ' +
        'Operators can register a name via the Solana Config program.',
    };
  }

  const tier = tierFromProvenance(provenance);

  let tooltip: string;
  switch (tier) {
    case 'verified':
      tooltip =
        provenance === 'keybase'
          ? `Verified — Keybase identity (@${name}).`
          : 'Verified — on-chain Config program metadata.';
      break;
    case 'inferred':
      tooltip =
        'Inferred from gossip / provider + country metadata. ' +
        'Auto-generated label; this validator has not published on-chain Config metadata.';
      break;
    case 'unverified':
      tooltip =
        'Unverified — no registered name. ' +
        'This validator has not published on-chain Config metadata.';
      break;
  }

  return {
    text: name,
    tier,
    provenance,
    isPubkey: false,
    tooltip,
  };
}
